import React, { useState, useEffect } from 'react';
import { Container } from '../ui/Container';
import { Button } from '../ui/Button';
import { cn } from '../../lib/cn';
import { getImagePath } from '../../Utils/Utilities';
import { useIsMobile } from '../../Hooks/useIsMobile';

// Sub-component for the Detail Dialog
const FeatureDetailModal = ({ isOpen, onClose, feature, onBuyNow }) => {
  const [currentImg, setCurrentImg] = useState(0);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isOpen) {
      setCurrentImg(0);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  if (!isOpen || !feature) return null;

  const nextSlide = () => setCurrentImg((prev) => (prev === feature.screens.length - 1 ? 0 : prev + 1));
  const prevSlide = () => setCurrentImg((prev) => (prev === 0 ? feature.screens.length - 1 : prev - 1));

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-[#f5f5f5] w-full max-w-6xl rounded shadow-2xl relative overflow-hidden flex flex-col max-h-[95vh]" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 md:px-6 py-4 md:py-6 flex justify-between items-start border-b-0">
          <div className="text-left flex-1 min-w-0 pr-3">
            <h2 className="text-xl md:text-3xl font-bold text-[#333] mb-1 leading-tight">{feature.title}</h2>
            <p className="text-sm md:text-[20px] leading-7 font-semibold text-text-primary">{feature.tagline}</p>
          </div>
          <div className="flex items-center gap-8 flex-shrink-0">
            <Button size="sm" className="hidden md:inline-flex" onClick={onBuyNow}>Buy Now</Button>
            <button onClick={onClose} className="text-3xl text-gray-400 hover:text-black leading-none">&times;</button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 md:gap-8 p-4 md:p-8 pt-2 md:pt-0 overflow-y-auto">
          {/* Screenshot first on mobile, right side on desktop */}
          <div className="w-full md:w-[65%] md:order-2 bg-white rounded-xl shadow-inner border border-gray-100 p-3 md:p-4 relative group flex flex-col items-center">
            <h4 className="text-center font-bold text-gray-700 mb-3 text-sm md:text-lg">{feature.screens[currentImg]?.sTitle}</h4>
            <div className="relative w-full flex items-center justify-center px-8">
              <button onClick={prevSlide} className="absolute left-0 text-primary text-5xl md:text-6xl opacity-70 hover:opacity-100 cursor-pointer">‹</button>
              <img src={feature.screens[currentImg]?.sPic} alt="Screenshot" className="max-w-full h-auto max-h-[220px] md:max-h-[400px] object-contain rounded shadow-sm" />
              <button onClick={nextSlide} className="absolute right-0 text-primary text-5xl md:text-6xl opacity-70 hover:opacity-100 cursor-pointer">›</button>
            </div>
            <div className="flex gap-2 mt-4">
              {feature.screens.map((_, i) => (
                <div key={i} className={`h-1.5 w-1.5 md:h-2 md:w-2 rounded-full ${i === currentImg ? 'bg-[#e88421]' : 'bg-gray-300'}`} />
              ))}
            </div>
          </div>

          {/* Feature list - below screenshot on mobile */}
          <div className="w-full md:w-[35%] md:order-1 flex flex-col">
            <div className="flex flex-wrap gap-x-4 gap-y-2 mb-4 md:mb-8">
              {feature.items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-1.5 text-[14px] md:text-[15px] text-text-primary">
                  <span className="text-primary font-bold text-lg md:text-xl flex-shrink-0 leading-none">✓</span>
                  <span className='md:text-[17px]' dangerouslySetInnerHTML={{ __html: item }} />
                </div>
              ))}
            </div>
            <Button size="sm" className="w-fit" onClick={onBuyNow}>Buy Now</Button>
          </div>
        </div>
      </div>
    </div>
  );
};
const ProductFeaturesSection = ({ onBuyNow, onFeatureClick }) => {
  const [openFeature, setOpenFeature] = useState(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const isMobile = useIsMobile()
  const [closedDesktopFeatures, setClosedDesktopFeatures] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const handleModalBuyNow = () => {
    setSelectedFeature(null);
    onBuyNow?.();
  };

  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  const features = [
    {
      id: 'crm',
      title: 'Leads CRM',
      tagline: 'Convert more Inquiries to Sales!',
      items: [
        'IndiaMart', 'TradeIndia', 'JustDial', 'Meta forms', '99Acres',
        'MagicBricks', 'Website API', 'Lead Assignment', 'Funnel with Stages',
        'Rejection Analysis', 'Interactions', 'Follow-up', 'Visit Geo-Tag'
      ],
      screens: [
        { sTitle: 'Lead Management', sPic: getImagePath('/Images/Features/LeadMgmt.png') },
        { sTitle: 'Lead Integrations', sPic: getImagePath('/Images/Features/LeadIntegrations.png') },
        { sTitle: 'Prospect Funnel', sPic: getImagePath('/Images/Features/LeadProspFunnel.png') },
        { sTitle: 'Interactions & Appointments', sPic: getImagePath('/Images/Features/LeadProspInxns.png') },
        { sTitle: 'Travel History Log', sPic: getImagePath('/Images/Features/LeadProspTravelHistory.png') }
      ]
    },
    {
      id: 'quotations',
      title: 'Quotations',
      tagline: 'Impress customers before the deal is done!',
      items: [
        // 'Quotations with Product Images', 'Proforma Invoice', 'Multiple Formats',
        // 'WhatsApp & Email Share', 'Convert to Sales Order', 'Revision Tracking',
        // 'Custom Terms & Conditions', 'Digital Signature Support'
        'Create Quotations & Proforma Invoices',
        'Share via WhatsApp web or Email',
        'Manage Templates and Revisions',
        'Monitor Opens & Conversions',
      ],
      screens: [
        { sTitle: 'Create Quotations with Images', sPic: getImagePath('/Images/Features/InvoiceList.png') },
        { sTitle: 'Share via WhatsApp, Email', sPic: getImagePath('/Images/Features/InvoiceShare.png') },
        { sTitle: 'Proforma Invoice Sample', sPic: getImagePath('/Images/Features/InvoicePrintB2B.png') },
        { sTitle: 'Deliver Impressively with Payment Options', sPic: getImagePath('/Images/Features/InvoiceTP.png') },
      ]
    },
    {
      id: 'sales-orders',
      title: 'Orders',
      tagline: 'Track every order from booking to delivery!',
      items: [
        // 'Sales Order Creation', 'Order Status Updates to Customers',
        // 'Delivery Scheduling', 'Pending Order Tracking',
        // 'Convert from Quotation', 'WhatsApp & Email Notifications',
        // 'Order-wise Profitability', 'Integrated with Billing'
        'Send Smart Acknowledgement',
        'Give Status Updates',
        'Send Dispatch notification',
        'Get Feedback & Google Rating',
      ],
      screens: [
        { sTitle: 'Manage Sales Orders', sPic: getImagePath('/Images/Features/InvoiceList.png') },
        { sTitle: 'Customize Document Series', sPic: getImagePath('/Images/Features/InvoiceDocSeries.png') },
        { sTitle: 'Share via WhatsApp, Email', sPic: getImagePath('/Images/Features/InvoiceShare.png') },
        { sTitle: 'Monitor through Reports', sPic: getImagePath('/Images/Features/InvoiceReports.png') },
      ]
    },
    {
      id: 'billing',
      title: 'Billing',
      tagline: 'Bill faster, get paid sooner!',
      items: [
        // 'GST Billing (B2B & B2C)', 'POS / Cash Memo Print',
        // 'E-Invoice Generation', 'E-Way Bill', 'Invoice Export to Tally',
        // 'Integrated with Accounts', 'GSTR-1 & GSTR-3B JSONs',
        // 'Multiple Tax Slabs', 'Discount & Scheme Management'
        'Generate GST invoices',
        'Link with POS for Retail',
        'Send via WhatsApp web or Email',
        'Manage EWB, e-Invoice, GST',
      ],
      screens: [
        { sTitle: 'Manage Invoices (B2B, POS, Export)', sPic: getImagePath('/Images/Features/InvoiceList.png') },
        { sTitle: 'GST Invoice Entry (B2B)', sPic: getImagePath('/Images/Features/InvoiceEntryB2B.png') },
        { sTitle: 'Invoice Print Sample (B2B)', sPic: getImagePath('/Images/Features/InvoicePrintB2B.png') },
        { sTitle: 'Invoice Print Sample (POS)', sPic: getImagePath('/Images/Features/InvoicePrintCashMemo.png') },
        { sTitle: 'Deliver Invoices with Easy Payment Options', sPic: getImagePath('/Images/Features/InvoiceTP.png') },
        { sTitle: 'GST Compliance (GSTR-1 & 3B JSONs)', sPic: getImagePath('/Images/Features/InvoiceGSTR3B.png') },
        { sTitle: 'Analyze Trade Profitability', sPic: getImagePath('/Images/Features/InvoiceRptTradeProfitability.png') },
        { sTitle: 'Monitor through Reports', sPic: getImagePath('/Images/Features/InvoiceReports.png') },
      ]
    },
    {
      id: 'recovery',
      title: 'Recovery',
      tagline: 'Get paid on time, every time!',
      items: [
        // 'Receivables Dashboard', 'Overdue Tracking', 'Payment Reminders via WhatsApp',
        // 'Receipt Entry', 'Party-wise Outstanding', 'Ageing Analysis',
        // 'Integrated with Accounts', 'Collection Follow-up Logs'
        'Track Bill-wise Receivables',
        'Send Follow-up Reminders',
        'Link to RazorPay gateway',
        'Send Thank You note on receipt',
      ],
      screens: [
        { sTitle: 'Track Receivables & Outstanding', sPic: getImagePath('/Images/Features/InvoiceList.png') },
        { sTitle: 'Share Payment Reminders via WhatsApp', sPic: getImagePath('/Images/Features/InvoiceShare.png') },
        { sTitle: 'Deliver Invoice with Payment Link', sPic: getImagePath('/Images/Features/InvoiceTP.png') },
        { sTitle: 'Monitor through Reports', sPic: getImagePath('/Images/Features/InvoiceReports.png') },
      ]
    },
    {
      id: 'accounts',
      title: 'Accounts',
      tagline: 'Monitor from your Mobile!',
      items: [
        // 'Balance Sheet', 'P&L', 'Trial Balance', 'Vouchers', 'Ledger Record',
        // 'Ledger Import', 'Sales Billing', 'GSTR-1', 'GSTR-3B',
        // 'E-Way Bill direct', 'E-Invoice generation', 'Purchases', 'Mobile Access (even voucher entry)'
        'Enter Vouchers even from mobile',
        'Track Key Ledgers easily',
        'Get Balance Sheet, P&L, etc.',
        'Generate GSTR-1 & 3B JSON',
      ],
      screens: [
        { sTitle: 'CEO-friendly Easy Accounting', sPic: getImagePath('/Images/Features/AccountsDsbd.png') },
        { sTitle: 'Access Real-time Accounts from your Mobile', sPic: getImagePath('/Images/Features/AccountsDsbdMob.png') },
        { sTitle: 'Enter Vouchers Easily', sPic: getImagePath('/Images/Features/AccountsVoucherEntryMob.png') },
        { sTitle: 'Access Live Statements', sPic: getImagePath('/Images/Features/AccountsStatements.png') },
        { sTitle: 'Access Live Statements (Mobile)', sPic: getImagePath('/Images/Features/AccountsP&LMob.png') },
        { sTitle: 'Simplify GST Compliance', sPic: getImagePath('/Images/Features/InvoiceGSTR3B.png') },
        { sTitle: 'Manage Multiple Branches', sPic: getImagePath('/Images/Features/AccountsMultiBranch.png') },
        { sTitle: 'Monitor through Reports', sPic: getImagePath('/Images/Features/AccountsMultiBranch.png') },
      ]
    },
    {
      id: 'purchase',
      title: 'Purchase',
      tagline: 'Buy smart, stock right!',
      items: [
        // 'Purchase Orders', 'Vendor Comparison', 'GRN (Goods Receipt Note)',
        // 'Purchase Returns', 'Delivery Follow-up', 'Purchase Planning from Sales',
        // 'Integrated with Inventory & Accounts', 'Vendor Payment Tracking'
        'Generate Purchase Orders',
        'Get Status Update from Suppliers',
        'Enter GRN on material receipt',
        'Plan based on Inventory Shortfall',
      ],
      screens: [
        { sTitle: 'Track Inventory Stock', sPic: getImagePath('/Images/Features/InventoryStockList.png') },
        { sTitle: 'Observe Items with Insufficient Stock', sPic: getImagePath('/Images/Features/InventoryInsufficient.png') },
        { sTitle: 'Manage BOM (Bill of Materials)', sPic: getImagePath('/Images/Features/InventoryBOM.png') },
        { sTitle: 'Monitor through Reports', sPic: getImagePath('/Images/Features/InventoryReports.png') },
      ]
    },
    {
      id: 'inventory',
      title: 'Inventory',
      tagline: 'Stock smartly!',
      items: [
        'Stock View', 'Batches', 'Expiry', 'Min Quantity levels', 'BOM',
        'Purchase Planning', 'Purchase Orders', 'Delivery Follow-up', 'GRN',
        'Material Issue', 'Physical Stock Reconciliation', 'Item-wise Transaction History',
        'Integrated with Accounts module', 'Multiple Stores'
      ],
      screens: [
        { sTitle: 'Track Inventory Stock', sPic: getImagePath('/Images/Features/InventoryStockList.png') },
        { sTitle: 'Manage Stock at Multiple Stores', sPic: getImagePath('/Images/Features/InventoryStores.png') },
        { sTitle: 'Observe Items with Insufficient Stock', sPic: getImagePath('/Images/Features/InventoryInsufficient.png') },
        { sTitle: 'Easy Item Entry', sPic: getImagePath('/Images/Features/InventoryItemEntry.png') },
        { sTitle: 'Log Batches & Expiry Dates', sPic: getImagePath('/Images/Features/InventoryItemBatches.png') },
        { sTitle: 'Manage BOM (Bill of Materials)', sPic: getImagePath('/Images/Features/InventoryBOM.png') },
        { sTitle: 'Track Inventory Transactions', sPic: getImagePath('/Images/Features/InventoryXnTypes.png') },
        { sTitle: 'Monitor through Reports', sPic: getImagePath('/Images/Features/InventoryReports.png') },
      ]
    },
    {
      id: 'manufacturing',
      title: 'Production',
      tagline: 'Make (smartly) in India!',
      items: [
        'Launch Production Jobs',
        'Track WIP at Process Stages',
        'Monitor Material Consumption vs BOM',
        'Analyze Rejection Reasons',
      ],
      screens: [
        { sTitle: 'Monitor Manufacturing (WIP) Jobs', sPic: getImagePath('/Images/Features/ProductionList.png') },
        { sTitle: 'Track Job Status against Plan', sPic: getImagePath('/Images/Features/ProductionJobTracking.png') },
        { sTitle: 'Easy Production Job Entry', sPic: getImagePath('/Images/Features/ProductionJobEntry.png') },
        { sTitle: 'Track Job Status, Monitor Issued Materials', sPic: getImagePath('/Images/Features/ProductionJobDetails.png') },
        { sTitle: 'Update Job Stages', sPic: getImagePath('/Images/Features/ProductionJobMovement.png') },
        { sTitle: 'Receive Finished Goods at Job Completion', sPic: getImagePath('/Images/Features/ProductionReceiveFG.png') },
        { sTitle: 'Return Unused Material to Store', sPic: getImagePath('/Images/Features/ProductionReceiveUnused.png') },
        { sTitle: 'Manufacturing Job Card Print Sample', sPic: getImagePath('/Images/Features/ProductionJobCard.png') },
        { sTitle: 'Monitor through Reports', sPic: getImagePath('/Images/Features/ProductionReports.png') },
      ]
    },
    {
      id: 'support',
      title: 'Support',
      tagline: 'Keep your customers happy, always!',
      items: [
        'Support Ticket Creation', 'Ticket Assignment to Team',
        'Priority & Status Tracking', 'Customer Notifications',
        'Resolution Logs', 'SLA Tracking', 'Mobile Access', 'Reports'
      ],
      screens: [
        { sTitle: 'Task Management', sPic: getImagePath('/Images/Features/TasksDesktop.png') },
        { sTitle: 'Track Job Status', sPic: getImagePath('/Images/Features/ProductionJobTracking.png') },
        { sTitle: 'Interactions & Appointments', sPic: getImagePath('/Images/Features/LeadProspInxns.png') },
      ]
    },
    {
      id: 'contracts',
      title: 'Contracts',
      tagline: 'Never miss a renewal again!',
      items: [
        'Contract & AMC Creation', 'Renewal Date Tracking',
        'Expiry Alerts & Notifications', 'Customer-wise Contract View',
        'Service History Logs', 'Document Attachment', 'Reports'
      ],
      screens: [
        { sTitle: 'Contract & AMC Tracking', sPic: getImagePath('/Images/Features/InvoiceTP.png') },
        { sTitle: 'Share via WhatsApp, Email', sPic: getImagePath('/Images/Features/InvoiceShare.png') },
        { sTitle: 'Monitor through Reports', sPic: getImagePath('/Images/Features/InvoiceReports.png') },
      ]
    },
    {
      id: 'tasks',
      title: 'Tasks',
      tagline: 'Stay Organized!',
      items: [
        'Finish own tasks (Inbox)',
        'Monitor delegated tasks (Outbox)',
        'Log completed/paused tasks',
        'Remind for Follow-up',
      ],
      screens: [
        { sTitle: 'Task Management', sPic: getImagePath('/Images/Features/TasksDesktop.png') },
        { sTitle: 'Project Tracking', sPic: getImagePath('/Images/Features/ProductionJobTracking.png') },
      ]
    },
    {
      title: 'CRM',
      tagline: 'Convert more Inquiries to Sales!',
      desktop: true,
      items: [
        'IndiaMart', 'TradeIndia', 'JustDial', 'Meta forms', '99Acres',
        'MagicBricks', 'Website API', 'Lead Assignment', 'Funnel with Stages',
        'Rejection Analysis', 'Interactions', 'Follow-up', 'Visit Geo-Tag'
        // 'Import from <b>IndiaMart, TradeIndia, JustDial, Meta (FB)</b>',
        // '<b>Assign</b> to Team, monitor <b>Interactions</b> & visits',
        // 'Track prospects in <b>stages</b> of conversion',
        // 'Analyze <b>performance</b> & <b>rejections</b>'
      ],
      screens: [
        { sTitle: 'Lead Management', sPic: getImagePath('/Images/Features/LeadMgmt.png') },
        { sTitle: 'Lead Integrations', sPic: getImagePath('/Images/Features/LeadIntegrations.png') },
        { sTitle: 'Prospect Funnel', sPic: getImagePath('/Images/Features/LeadProspFunnel.png') },
        { sTitle: 'Interactions & Appointments', sPic: getImagePath('/Images/Features/LeadProspInxns.png') },
        { sTitle: 'Travel History Log', sPic: getImagePath('/Images/Features/LeadProspTravelHistory.png') }
      ]
    },
    {
      id: 'sales',
      title: 'Sales & Billing',
      desktop: true,

      tagline: 'Streamline your complete sales cycle',
      items: [
        'Quotations (with images)', 'Proforma Invoice', 'Sales Orders',
        'Status Update to Customers', 'GST Billing', 'POS Print',
        'Receivables', 'Invoice export to Tally', 'Integrated with Accounts module',
        'E-Way Bill', 'E-Invoice'
        // 'Quotations',
        // 'Orders',
        // 'Billing & GST Invoicing',
        // 'POS System',
        // 'E-Invoice Generation',
        // 'Recovery Management'
      ],
      screens: [
        { sTitle: 'Manage Invoices (B2B, POS, Export)', sPic: getImagePath('/Images/Features/InvoiceList.png') },
        { sTitle: 'Generate E-way Bills', sPic: getImagePath('/Images/Features/InvoiceEntryB2B.png') },
        { sTitle: 'Customize Auto Document Series', sPic: getImagePath('/Images/Features/InvoiceDocSeries.png') },
        { sTitle: 'Share via WhatsApp, Email', sPic: getImagePath('/Images/Features/InvoiceShare.png') },
        { sTitle: 'Invoice Print Sample (B2B)', sPic: getImagePath('/Images/Features/InvoicePrintB2B.png') },
        { sTitle: 'Invoice Print Sample (POS)', sPic: getImagePath('/Images/Features/InvoicePrintCashMemo.png') },
        { sTitle: 'Share via WhatsApp, Email', sPic: getImagePath('/Images/Features/InvoiceDocSeries.png') },
        { sTitle: 'Deliver Invoices Impressively & Offer Easy Payment Options', sPic: getImagePath('/Images/Features/InvoiceTP.png') },
        { sTitle: 'Analyze Trade Profitability', sPic: getImagePath('/Images/Features/InvoiceRptTradeProfitability.png') },
        { sTitle: 'Monitor through Reports', sPic: getImagePath('/Images/Features/InvoiceReports.png') },
        { sTitle: 'GST Compliance (GSTR-1 & 3B JSONs)', sPic: getImagePath('/Images/Features/InvoiceGSTR3B.png') },
      ]
    },
    {
      id: 'inventory',
      title: 'Inventory',
      tagline: 'Stock smartly!',
      desktop: true,

      items: [
        'Stock View', 'Batches', 'Expiry', 'Min Quantity levels', 'BOM',
        'Purchase Planning', 'Purchase Orders', 'Delivery Follow-up', 'GRN',
        'Material Issue', 'Physical Stock Reconciliation', 'Item-wise Transaction History',
        'Integrated with Accounts module', 'Multiple Stores'
        // 'Manage Multiple <b>Stores</b>',
        // 'Maintain <b>Batches</b>(with expiry)',
        // 'Get <b>Transaction</b> History',
        // '<b>Plan</b> Purchase in time'
      ],
      screens: [
        { sTitle: 'Track Inventory Stock', sPic: getImagePath('/Images/Features/InventoryStockList.png') },
        { sTitle: 'Manage Stock at Multiple Stores', sPic: getImagePath('/Images/Features/InventoryStores.png') },
        { sTitle: 'Observe Items with Insufficient Stock', sPic: getImagePath('/Images/Features/InventoryInsufficient.png') },
        { sTitle: 'Easy Item Entry', sPic: getImagePath('/Images/Features/InventoryItemEntry.png') },
        { sTitle: 'Log Batches & Expiry Dates', sPic: getImagePath('/Images/Features/InventoryItemBatches.png') },
        { sTitle: 'Manage BOM (Bill of Materials)', sPic: getImagePath('/Images/Features/InventoryBOM.png') },
        { sTitle: 'Track Inventory Transactions', sPic: getImagePath('/Images/Features/InventoryXnTypes.png') },
        { sTitle: 'Monitor through Reports', sPic: getImagePath('/Images/Features/InventoryReports.png') },
      ]
    },
    {
      id: 'manufacturing',
      title: 'Manufacturing',
      tagline: 'Make (smartly) in India!',
      desktop: true,

      items: [
        'Production Jobs', 'BOM', 'MRP', 'WIP Stage Movement',
        'Material Consumption vs BOM', 'Material Availability',
        'Rejection Analysis', 'Backflush', 'Finished Goods', 'Batches'
        // 'Launch <b>Production Jobs</b>',
        // 'Track WIP at <b>Process Stages</b>',
        // 'Monitor Material <b>Consumption</b> vs <b>BOM</b>',
        // 'Analyze <b>Rejection</b> Reasons'
      ],
      screens: [
        { sTitle: 'Monitor Manufacturing (WIP) Jobs', sPic: getImagePath('/Images/Features/ProductionList.png') },
        { sTitle: 'Track Job Status against Plan', sPic: getImagePath('/Images/Features/ProductionJobTracking.png') },
        { sTitle: 'Easy Production Job Entry', sPic: getImagePath('/Images/Features/ProductionJobEntry.png') },
        { sTitle: 'Track Job Status, Monitor Issued Materials', sPic: getImagePath('/Images/Features/ProductionJobDetails.png') },
        { sTitle: 'Update Job Stages', sPic: getImagePath('/Images/Features/ProductionJobMovement.png') },
        { sTitle: 'Receive Finished Goods at Job Completion', sPic: getImagePath('/Images/Features/ProductionReceiveFG.png') },
        { sTitle: 'Return Unused Material to Store', sPic: getImagePath('/Images/Features/ProductionReceiveUnused.png') },
        { sTitle: 'Manufacturing Job Card Print Sample', sPic: getImagePath('/Images/Features/ProductionJobCard.png') },
        { sTitle: 'Monitor through Reports', sPic: getImagePath('/Images/Features/ProductionReports.png') },
      ]
    },
    {
      id: 'accounts',
      title: 'Accounts',
      tagline: 'Monitor from your Mobile!',
      desktop: true,

      items: [
        'Balance Sheet', 'P&L', 'Trial Balance', 'Vouchers', 'Ledger Record',
        'Ledger Import', 'Sales Billing', 'GSTR-1', 'GSTR-3B',
        'E-Way Bill direct', 'E-Invoice generation', 'Purchases', 'Mobile Access (even voucher entry)'
        // 'Enter Vouchers even from <b>mobile</b>',
        // 'Track Key <b>Ledgers</b> easily',
        // 'Get <b>Balance Sheet, P&L,</b> etc.',
        // 'Generate <b>GSTR-1 & 3B</b> JSON'
      ],
      screens: [
        { sTitle: 'CEO-friendly Easy Accounting', sPic: getImagePath('/Images/Features/AccountsDsbd.png') },
        { sTitle: 'Access Real-time Accounts from your Mobile', sPic: getImagePath('/Images/Features/AccountsDsbdMob.png') },
        { sTitle: 'Enter Vouchers Easily', sPic: getImagePath('/Images/Features/AccountsVoucherEntryMob.png') },
        { sTitle: 'Enter Vouchers (Mobile)', sPic: getImagePath('/Images/Features/ProductionJobDetails.png') },
        { sTitle: 'Access Live Statements', sPic: getImagePath('/Images/Features/AccountsStatements.png') },
        { sTitle: 'Access Live Statements (Mobile)', sPic: getImagePath('/Images/Features/AccountsP&LMob.png') },
        { sTitle: 'Simplify GST Compiance', sPic: getImagePath('/Images/Features/InvoiceGSTR3B.png') },
        { sTitle: 'Manage Multiple Branches', sPic: getImagePath('/Images/Features/AccountsMultiBranch.png') },
        { sTitle: 'Monitor through Reports', sPic: getImagePath('/Images/Features/AccountsMultiBranch.png') },
      ]
    },
    {
      id: 'more',
      desktop: true,

      title: 'And More!',
      tagline: 'Everything else your business needs',
      items: [
        'Project Tracking', 'Task Delegation', 'Own Tasks', 'Web Profile',
        'Customer Login', 'Status Updates', 'Supplier follow-up',
        'Support Ticketing', 'Contracts & AMC Tracking'
      ],
      screens: [
        { sTitle: 'Project Tracking', sPic: getImagePath('/Images/Features/ProductionJobTracking.png') },
        { sTitle: 'Task Management', sPic: getImagePath('/Images/Features/TasksDesktop.png') },
        // { sTitle: 'Customer Web Profile', sPic: getImagePath('/Images/Features/AccountsDsbdMob.png') },
        // { sTitle: 'Customer Login Portal', sPic: getImagePath('/Images/Features/AccountsDsbd.png') },
        // { sTitle: 'Support Ticketing', sPic: getImagePath('/Images/Features/LeadProspInxns.png') },
        // { sTitle: 'Contracts & AMC Tracking', sPic: getImagePath('/Images/Features/InvoiceTP.png') },
        // { sTitle: 'Supplier Follow-up', sPic: getImagePath('/Images/Features/InventoryInsufficient.png') },
        // { sTitle: 'Status Updates', sPic: getImagePath('/Images/Features/InvoiceShare.png') },
        // { sTitle: 'Monitor through Reports', sPic: getImagePath('/Images/Features/InvoiceReports.png') },
      ]
    }
  ];

  const toggleFeature = (id) => {
    if (!isDesktop) {
      setOpenFeature(openFeature === id ? null : id);
    } else {
      setClosedDesktopFeatures(prev =>
        prev.includes(id)
          ? prev.filter(featId => featId !== id)
          : [...prev, id]
      );
    }
  };

  const isFeatureOpen = (id) => {
    if (isDesktop) {
      return !closedDesktopFeatures.includes(id);
    }
    return openFeature === id;
  };

  return (
    <section id="productfeaturessection" className={`${!isMobile ? 'bg-white' : 'bg-background-light'} border-top py-12 md:py-16 lg:py-20`}>
      <Container>
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <h3 className="text-[30px] md:text-[40px] font-bold secttl mb-0 px-6 md:px-10 pb-2">
            Features
          </h3>
          <div className="text-[14px] md:text-lg text-text-primary px-14">
            {isMobile ? <img src={getImagePath("/Images/Icons/HeroIcon.png")} /> : <span>Click any ERP module below to explore details of its <b>Features</b> and preview its <b>Sample Screenshots</b>.</span>}

          </div>
        </div>

        {/* Mobile Icon Grid */}
        <div className="md:hidden grid grid-cols-3 px-4 gap-[15px] mb-8 mx-auto">
          {[
            { id: 'crm', title: 'CRM', img: '/Images/Icons/Leads.png' },
            { id: 'quotations', title: 'Quotations', img: '/Images/Icons/Quotations.png' },
            { id: 'sales-orders', title: 'Orders', img: '/Images/Icons/Orders.png' },
            { id: 'billing', title: 'Billing', img: '/Images/Icons/Billing.png' },
            { id: 'recovery', title: 'Recovery', img: '/Images/Icons/Payment Recovery.png' },
            { id: 'accounts', title: 'Accounts', img: '/Images/Icons/Accounts.png' },
            { id: 'purchase', title: 'Purchase', img: '/Images/Icons/Purchase.png' },
            { id: 'inventory', title: 'Inventory', img: '/Images/Icons/Inventory.png' },
            { id: 'manufacturing', title: 'Production', img: '/Images/Icons/Manufacturing.png' },
            { id: 'support', title: 'Support', img: '/Images/Icons/Support Ticketing.png' },
            { id: 'contracts', title: 'Contracts', img: '/Images/Icons/Contracts.png' },
            { id: 'tasks', title: 'Tasks', img: '/Images/Icons/Tasks.png' },
          ].map((item) => {
            const matched = features.find(f => f.id === item.id);
            return (
              <div
                key={item.id}
                className="bg-white rounded border border-border flex flex-col items-center justify-center p-[10px] w-full"
                onClick={() => { }}
              >
                <img src={getImagePath(item.img)} alt={item.title} className={`w-[40px] object-contain mb-2 pt-2 ${item.id == "crm" ? 'pt-4' : ''}`} />
                <span className="text-[15px] font-semibold text-text-primary text-center leading-tight">{item.title}</span>
              </div>
            );
          })}
        </div>

        {/* Mobile Feature Summary Box */}
        <div className="md:hidden px-4">
          <div className="bg-white rounded border border-border p-4 space-y-5">
            {[
              {
                title: 'Growth Tools',
                items: ['Lead Import', 'Funnel', 'Assignment', 'Interactions', 'Follow-ups', 'Visit Geo-Tag', 'Quotations', 'Proforma Invoices', 'Sales Orders', 'Billing', 'POS Print', 'Contracts', 'AMCs', 'Support Ticketing', 'Status Updates', 'Customer Login']
              },
              {
                title: 'Smart Operations',
                items: ['Inventory', 'Multiple Stores', 'Batches', 'Expiry', 'Min levels', 'BOM', 'Purchase Planning', 'Purchase Orders', 'GRN', 'Material Issue', 'Stock Reconciliation', 'Production Jobs', 'MRP', 'WIP Stage Movement', 'Consumption', 'Stock Availability', 'Rejection Analysis', 'Backflush', 'Projects', 'Tasks']
              },
              {
                title: 'Easy Accounting',
                items: ['Balance Sheet', 'P&L', 'Trial Balance', 'Vouchers', 'Ledger Record', 'Ledger Import', 'Sales Billing', 'GSTR-1', 'GSTR-3B', 'E-Way Bill', 'E-Invoice', 'Purchases', 'Mobile Access & Entry']
              },
              {
                title: 'Integrations',
                items: ['IndiaMart', 'TradeIndia', 'JustDial', 'Meta forms', '99Acres', 'MagicBricks', 'Website API', 'Tally (invoices)', 'Voucher import', 'Excel export', 'Gmail (outbound)', 'E-Way Bill', 'E-Invoice']
              },
            ].map((section, idx, arr) => (
              <div key={section.title}>
                <h4 className="text-[15px] font-bold text-text-primary mb-3">{section.title}</h4>
                <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                  {section.items.map((item, i) => (
                    <div key={i} className="flex items-center gap-1 text-[13px] text-text-primary">
                      <span className="text-primary font-bold leading-none">✓</span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}


          </div>
        </div>

        {/* Accordion Items - Desktop only */}
        <div className="max-w-6xl mx-auto">
          {/* First Row - 3 items */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
            {features.filter(f => f.desktop).slice(0, 3).map((feature) => (
              <div
                key={feature.id}
                className={cn(
                  "bg-background-light rounded shadow-light overflow-hidden flex flex-col transition-all duration-300",
                  isFeatureOpen(feature.id) ? "h-full" : "h-fit"
                )}
              >
                <a
                  onClick={() => toggleFeature(feature.id)}
                  className="w-full px-5 md:px-6 py-4 flex justify-between items-center hover:bg-gray-50 transition-colors md:cursor-default md:hover:bg-white md:pointer-events-none"
                >
                  <h3 className="text-lg md:text-[22px] text-primary text-left font-bold m-0">
                    {feature.title}
                  </h3>
                  <span className="text-[rgba(0,0,0,0.3)] text-sm md:text-base md:hidden">
                    {isFeatureOpen(feature.id) ? (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </span>
                </a>

                {/* Content Area */}
                <div
                  className={`transition-all duration-300 ease-in-out overflow-hidden flex flex-col flex-grow md:max-h-[1000px] md:opacity-100 ${isFeatureOpen(feature.id) ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                >
                  <div className="px-5 md:px-6 pb-6 pt-2 flex flex-col flex-grow">
                    <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-5">
                      {feature.items.map((item, index) => (
                        <div key={index} className="flex items-center text-sm md:text-base text-text-primary">
                          <span className="text-primary font-bold mr-1.5 flex-shrink-0 text-base leading-none">✓</span>
                          <span
                            className="font-medium leading-snug"
                            dangerouslySetInnerHTML={{ __html: item }}
                          />
                        </div>
                      ))}
                    </div>

                    <div className="mt-auto">
                      <Button onClick={() => { setSelectedFeature(feature); onFeatureClick?.(feature.title); }}>
                        Know More
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Second Row - Centered 2 items */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch mt-4">
            {features.filter(f => f.desktop).slice(3).map((feature) => (
              <div
                key={feature.id}
                className={cn(
                  "bg-background-light rounded shadow-light overflow-hidden flex flex-col transition-all duration-300 md:flex-1",
                  isFeatureOpen(feature.id) ? "h-full" : "h-fit"
                )}
              >
                <a
                  onClick={() => toggleFeature(feature.id)}
                  className="w-full px-5 md:px-6 py-4 flex justify-between items-center hover:bg-gray-50 transition-colors md:cursor-default md:hover:bg-white"
                >
                  <h3 className="text-lg md:text-[22px] text-primary text-left font-bold m-0">
                    {feature.title}
                  </h3>
                  <span className="text-[rgba(0,0,0,0.3)] text-sm md:text-base md:hidden">
                    {isFeatureOpen(feature.id) ? (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </span>
                </a>

                {/* Content Area */}
                <div
                  className={`transition-all duration-300 ease-in-out overflow-hidden flex flex-col flex-grow md:max-h-[1000px] md:opacity-100 ${isFeatureOpen(feature.id) ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                >
                  <div className="px-5 md:px-6 pb-6 pt-2 flex flex-col flex-grow">
                    <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-5">
                      {feature.items.map((item, index) => (
                        <div key={index} className="flex items-center text-sm md:text-base text-text-primary">
                          <span className="text-primary font-bold mr-1.5 flex-shrink-0 text-base leading-none">✓</span>
                          <span
                            className="font-medium leading-snug"
                            dangerouslySetInnerHTML={{ __html: item }}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="mt-auto">
                      <Button onClick={() => { setSelectedFeature(feature); onFeatureClick?.(feature.title); }}>
                        Know More
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Container>

      <FeatureDetailModal
        isOpen={!!selectedFeature}
        onClose={() => setSelectedFeature(null)}
        feature={selectedFeature}
        onBuyNow={handleModalBuyNow}
      />
    </section >
  );
};

export default ProductFeaturesSection;